import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../auth/auth-store.js";
import { useProfile } from "../store/profile.js";
import { composePresentationReplacement } from "./presentation-replacement.js";
import type { PresentationPrefs, PresentationTheme } from "./types.js";
import {
  presentationPreferencesQueryKey,
  usePresentationPreferencesQuery,
  useUpdatePresentationPreferences,
} from "./useSettings.js";

export type PresentationThemeStatus = "ready" | "pending" | "unavailable";
/** The initial read alone: unaffected by a write in flight (PB-5176 d9). */
export type PresentationReadStatus = "ready" | "pending" | "unavailable";

/**
 * Bridges Playbook BE's durable presentation preference onto the shell's
 * in-memory DOM projection (PB-5134, d17/d29).
 *
 * - `setTheme` is the Settings controls' write; `toggleDarkMode` is the
 *   top-bar toggle's, from the resolved look (high contrast toggles to light,
 *   Match device on a light device to an explicit dark). Both compose the PUT
 *   from the freshest cached row through `composePresentationReplacement`, so
 *   a toggle from an unpersisted row writes the detected zone, never "UTC".
 * - `status` lets controls disable instead of silently doing nothing while the
 *   read is pending, a write is in flight, or the read has failed; a failed
 *   read leaves the store's initial theme: the shared `pb_theme` cookie's, or
 *   `DEFAULT_PLATFORM_THEME` (Match device) when there is none. Settings ›
 *   Account reads it.
 * - `read` reports the initial read only. The top-bar toggle mounts on it
 *   (PB-5176 d9): gating on `status` would unmount the button on its own click
 *   and drop keyboard focus, because `status` is `pending` during the write.
 */
export function usePresentationTheme(): {
  readonly theme: PresentationTheme;
  readonly darkMode: boolean;
  readonly status: PresentationThemeStatus;
  readonly read: PresentationReadStatus;
  readonly setTheme: (next: PresentationTheme) => void;
  readonly toggleDarkMode: () => void;
} {
  const theme = useProfile((state) => state.theme);
  const darkMode = useProfile((state) => state.darkMode);
  const applyTheme = useProfile((state) => state.setTheme);
  const preferences = usePresentationPreferencesQuery();
  const { mutate, isPending: isWriting } = useUpdatePresentationPreferences();
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  const userId = useAuthStore((s) => s.claims?.userId);

  useEffect(() => {
    if (preferences.data) applyTheme(preferences.data.theme);
  }, [preferences.data, applyTheme]);

  const read: PresentationReadStatus = preferences.data
    ? "ready"
    : preferences.isPending
      ? "pending"
      : "unavailable";
  const status: PresentationThemeStatus =
    read === "ready" && isWriting ? "pending" : read;

  const setTheme = useCallback(
    (next: PresentationTheme) => {
      const current = queryClient.getQueryData<PresentationPrefs>(
        presentationPreferencesQueryKey(tenantId, userId),
      );
      if (!current || isWriting || current.theme === next) return;
      applyTheme(next);
      mutate(composePresentationReplacement(current, { theme: next }), {
        onError: () => applyTheme(current.theme),
      });
    },
    [applyTheme, isWriting, mutate, queryClient, tenantId, userId],
  );

  const toggleDarkMode = useCallback(() => {
    setTheme(darkMode ? "light" : "dark");
  }, [darkMode, setTheme]);

  return { theme, darkMode, status, read, setTheme, toggleDarkMode };
}
